/**
 * T2 Business Dashboard — the raw-material order book on one screen: what was ordered, how much of
 * it has landed, how well suppliers delivered it, and what is still outstanding.
 *
 * It summarises the same population the T2 Business Report's grids list, from the
 * same view, so the three cannot disagree. Everything on screen is a governed figure the server
 * computed; nothing here derives a measure, a ranking or a threshold.
 *
 * **SDP is on-time quantity over DELIVERED quantity**, quantity weighted, never a count of orders.
 * Early counts as on time and up to 3 days late still counts; 4 days and over does not. Orders
 * with no delivery yet are outstanding, not late — they leave the SDP denominator and are reported
 * as open instead of quietly depressing a score. The rule lives on the view as `is_on_time`.
 *
 * Five views, one per panel, because each is a different grain and every ratio among them is a
 * ratio of sums that cannot be re-derived from another panel's rows (§4.3).
 */

import { useMemo } from 'react';

import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ExportMenu } from '@/components/report/ExportMenu';
import { useReportFilters } from '@/components/report/useReportFilters';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryResponse } from '@/lib/api';

import { SupplierBoards } from './boards';
import { KpiCards } from './cards';
import { AgeingBars, FactoryBars } from './panels';
import { SupplierRing } from './sunburst';
import { t2BusinessDashboardFilters } from './filters';
import type {
  AgeingRow,
  FactoryRow,
  KpiRow,
  SupplierRingRow,
  SupplierSdpRow,
} from './contract';

const REPORT_ID = 't2-business-dashboard';

/**
 * Whether a response is the whole answer — the gate on every whole-result client transform (§4.3).
 * The boards take the two ends of a ranking and the ring folds a remainder, so both are withheld
 * rather than shown as if they covered the business.
 */
function isWholeResponse(response: ReportQueryResponse<unknown> | undefined): boolean {
  return response !== undefined && !response.truncated && response.rows.length === response.total;
}

export default function T2BusinessDashboardReport() {
  const schema = useMemo(() => t2BusinessDashboardFilters(), []);
  const { values, isComplete } = useReportFilters(schema);

  const enabled = isComplete;

  // Two views take no order state, and sending a filter outside a view's scope is a 400. The KPI
  // tiles report ordered, received and open side by side, and the ageing panel is always the open
  // book — neither has anything for the filter to choose, so `report.yaml` scopes it to the three
  // panels it does shape and the key is stripped here rather than made a no-op in their SQL.
  const wholeBookFilters = useMemo(() => {
    const rest = { ...values };
    delete rest.order_state;
    return rest;
  }, [values]);

  const kpis = useReportQuery<KpiRow>(REPORT_ID, wholeBookFilters, { view: 'kpis', enabled });
  const ring = useReportQuery<SupplierRingRow>(REPORT_ID, values, { view: 'by-supplier', enabled });
  const factories = useReportQuery<FactoryRow>(REPORT_ID, values, { view: 'by-factory', enabled });
  const ageing = useReportQuery<AgeingRow>(REPORT_ID, wholeBookFilters, { view: 'ageing', enabled });
  const suppliers = useReportQuery<SupplierSdpRow>(REPORT_ID, values, {
    view: 'supplier-sdp',
    enabled,
  });

  // Matches the filter schema's default, so the fallback and the control cannot disagree about
  // which measure the page opened on.
  const measure = values.measure ?? 'value';
  const orderState = values.order_state ?? 'ordered';

  const kpiRow = kpis.data?.rows[0];
  const ringRows = useMemo(() => ring.data?.rows ?? [], [ring.data?.rows]);
  const factoryRows = useMemo(() => factories.data?.rows ?? [], [factories.data?.rows]);
  const ageingRows = useMemo(() => ageing.data?.rows ?? [], [ageing.data?.rows]);
  const supplierRows = useMemo(() => suppliers.data?.rows ?? [], [suppliers.data?.rows]);

  return (
    <ReportPage
      title="T2 Business Dashboard"
      description="The raw material order book by supplier group, material type and factory."
      filters={
        <FilterBar
          schema={schema}
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={{ view: 'supplier-sdp', filters: values }}
              disabled={!enabled}
            />
          }
        />
      }
      table={
        <div className="bg-t2d-surface text-t2d-ink">
          <KpiCards row={kpiRow} measure={measure} isLoading={kpis.isLoading} />

          <div className="grid xl:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1.5fr)]">
            <SupplierRing
              rows={ringRows}
              measure={measure}
              orderState={orderState}
              complete={isWholeResponse(ring.data)}
            />
            <div className="border-t border-t2d-line xl:border-t-0 xl:border-l">
              <FactoryBars
                rows={factoryRows}
                measure={measure}
                orderState={orderState}
                complete={isWholeResponse(factories.data)}
              />
            </div>
            <div className="border-t border-t2d-line xl:border-t-0 xl:border-l">
              <AgeingBars rows={ageingRows} measure={measure} />
            </div>
          </div>

          <SupplierBoards
            rows={supplierRows}
            measure={measure}
            complete={isWholeResponse(suppliers.data)}
            isLoading={suppliers.isLoading}
          />

          {/*
            The reference's footer strip: the definitions a reader would otherwise carry in their
            head. The value/UOM caveat used to sit here too; removed on the owner's instruction
            2026-08-18, who is correcting the defect at source. It stays recorded in the server's
            docs/design/t2-vendor-analysis.md rather than on screen.
          */}
          <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-t2d-line px-6 py-2.5 text-xs text-t2d-muted">
            <span>SDP = quantity delivered on time divided by quantity delivered, up to 3 days late counts</span>
            <span>Orders not yet delivered are open, not late</span>
            <span>Measure and order state shape the three panels; the tiles always cover the whole book</span>
          </footer>
        </div>
      }
      isLoading={kpis.isLoading}
      error={kpis.error ?? ring.error ?? factories.error ?? ageing.error ?? suppliers.error}
      generatedAt={kpis.data?.generatedAt ?? null}
      dataAsOf={kpis.data?.dataAsOf ?? null}
      truncated={kpis.data?.truncated ?? false}
    />
  );
}
